import { Plus, Search, Trash2 } from 'lucide-react';
import * as React from 'react';

import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  Skeleton,
  toast,
} from '@/components/cargofl-ui';
import { Hint } from '@/components/cargofl-ui';
import { ConfirmDialog } from '@/components/confirm-dialog';

export interface LibraryItem {
  id: string;
  name: string;
  /** The one line under the name — count, scope, whatever distinguishes two entries. */
  subtitle: string;
  /**
   * False when somebody else authored this. Delete is hidden rather than shown and
   * then 403'd — the API is still the control, this only avoids a dead click.
   */
  canModify: boolean;
  /** Who owns it, when the caller does not. */
  ownerNote?: string;
}

/** A name that could become an entry but is not one yet — a designation with no KRA. */
export interface LibrarySuggestion {
  id: string;
  name: string;
  subtitle: string;
}

/**
 * The searchable pick-list both KRA tabs are built around.
 *
 * Shared rather than copied because the two tabs differ only in what they list: a
 * second copy is where the selected-row styling and the delete affordance drift apart.
 *
 * The search box is also the create box, the way GitHub's branch switcher is. Names
 * that exist but have no entry yet — the 41 designations with no KRA — surface here
 * while typing instead of being printed as 41 chips above the list: the same reach, at
 * no page height, and one that keeps working when there are 200 of them.
 */
export function LibraryList({
  items,
  isPending,
  selectedId,
  onSelect,
  onDelete,
  searchPlaceholder,
  emptyHint,
  suggestions = [],
  onCreate,
  createLabel = 'Create',
  bulkDelete,
  children,
}: {
  items: LibraryItem[];
  isPending: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete: (item: LibraryItem) => void;
  searchPlaceholder: string;
  emptyHint: string;
  /** Names that could become an entry. Shown only while searching, never by default. */
  suggestions?: LibrarySuggestion[];
  /** Enables the create row, called with the typed text or a picked suggestion. */
  onCreate?: (name: string) => void;
  createLabel?: string;
  /**
   * Tick-and-delete. Runs the caller's single delete once per row, so every server
   * check (ownership, "still used by a group") still applies, and a refused row is
   * reported by name rather than failing the batch.
   */
  bulkDelete?: {
    /** Plural noun for the copy: "KRAs", "groups". */
    noun: string;
    description: string;
    run: (id: string) => Promise<unknown>;
  };
  /**
   * The list's actions — "Create New …", bulk import.
   *
   * Rendered ABOVE the search box, not under the list. Under it they sat below 64
   * KRAs' worth of cards, which is far enough off screen that people asked for a
   * bulk import the page already had.
   */
  children?: React.ReactNode;
}) {
  const [query, setQuery] = React.useState('');
  const needle = query.trim().toLowerCase();
  const filtered = items.filter((item) => item.name.toLowerCase().includes(needle));
  const matchingSuggestions =
    needle === '' ? [] : suggestions.filter((s) => s.name.toLowerCase().includes(needle));
  // Never offer to create a name that is already in the library or already on offer
  // below — two KRAs called "Business Analyst" cannot be told apart afterwards.
  const duplicate = [...items, ...suggestions].some(
    (entry) => entry.name.trim().toLowerCase() === needle,
  );
  const creatable = onCreate !== undefined && needle !== '' && !duplicate;
  const nothingToShow =
    filtered.length === 0 && matchingSuggestions.length === 0 && !creatable;

  const [ticked, setTicked] = React.useState<Set<string>>(new Set());
  const [confirming, setConfirming] = React.useState(false);
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const [typed, setTyped] = React.useState('');
  const deletable = filtered.filter((item) => item.canModify);
  // Only what is still listed counts: a row deleted elsewhere drops out of the selection.
  const tickedItems = items.filter((item) => ticked.has(item.id));
  const allTicked = deletable.length > 0 && deletable.every((item) => ticked.has(item.id));

  function tick(id: string, on: boolean) {
    setTicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function runBulkDelete() {
    if (!bulkDelete) return;
    setBulkBusy(true);
    const failed: string[] = [];
    // Sequential: each delete invalidates the list, and a burst of parallel ones is
    // harder to read back as "these three were refused".
    for (const item of tickedItems) {
      try {
        await bulkDelete.run(item.id);
      } catch (e) {
        failed.push(`${item.name}: ${e instanceof Error ? e.message : 'failed'}`);
      }
    }
    setBulkBusy(false);
    setTicked(new Set());
    const deleted = tickedItems.length - failed.length;
    if (failed.length === 0) {
      toast.success(`Deleted ${deleted} ${bulkDelete.noun}`);
    } else {
      toast.error(`Deleted ${deleted}, ${failed.length} refused`, {
        description: <span className="whitespace-pre-line">{failed.join('\n')}</span>,
      });
    }
  }

  return (
    <div className="space-y-3">
      {children && <div className="grid gap-2">{children}</div>}

      {bulkDelete && deletable.length > 0 && (
        <div className="flex items-center gap-2 px-1">
          <Checkbox
            aria-label={`Select all ${bulkDelete.noun} shown`}
            checked={allTicked}
            onCheckedChange={(on) =>
              setTicked((prev) => {
                const next = new Set(prev);
                for (const item of deletable) {
                  if (on) next.add(item.id);
                  else next.delete(item.id);
                }
                return next;
              })
            }
          />
          <span className="flex-1 text-xs text-muted-foreground">
            {tickedItems.length > 0 ? `${tickedItems.length} selected` : 'Select all'}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-destructive"
            disabled={tickedItems.length === 0 || bulkBusy}
            onClick={() => setConfirming(true)}
          >
            <Trash2 className="size-3.5" />
            {bulkBusy ? 'Deleting…' : `Delete selected${tickedItems.length ? ` (${tickedItems.length})` : ''}`}
          </Button>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-8"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            // Enter on a query nothing matches creates it, like the branch switcher.
            if (e.key === 'Enter' && creatable && filtered.length === 0) {
              e.preventDefault();
              onCreate?.(query.trim());
              setQuery('');
            }
          }}
        />
      </div>

      {isPending && <Skeleton className="h-40 w-full" />}

      {!isPending && nothingToShow && (
        <p className="px-1 py-6 text-center text-xs text-muted-foreground">
          {items.length === 0 ? emptyHint : `Nothing matches “${query}”.`}
        </p>
      )}

      <div className="space-y-2">
        {filtered.map((item) => (
          <Card
            key={item.id}
            className={
              item.id === selectedId
                ? 'border-l-4 border-l-primary shadow-sm'
                : 'border-l-4 border-l-transparent'
            }
          >
            <CardContent className="flex items-center gap-2 p-3">
              {bulkDelete && item.canModify && (
                <Checkbox
                  aria-label={`Select ${item.name}`}
                  checked={ticked.has(item.id)}
                  onCheckedChange={(on) => tick(item.id, on === true)}
                />
              )}
              <Hint label={'Use this KRA'}><button type="button" className="flex-1 text-left" onClick={() => onSelect(item.id)}>
                <span className="text-sm font-medium">{item.name}</span>
                <span className="block text-xs text-muted-foreground">
                  {item.subtitle}
                  {item.ownerNote && <span className="block">{item.ownerNote}</span>}
                </span>
              </button></Hint>
              {item.canModify && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive"
                  aria-label={`Delete ${item.name}`}
                  onClick={() => onDelete(item)}
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {(matchingSuggestions.length > 0 || creatable) && (
        <div className="space-y-1 rounded-md border border-dashed p-1">
          {matchingSuggestions.map((suggestion) => (
            <Hint label={'Use this suggestion'}><button
              key={suggestion.id}
              type="button"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-accent"
              onClick={() => {
                onCreate?.(suggestion.name);
                setQuery('');
              }}
            >
              <Plus className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">{suggestion.name}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {suggestion.subtitle}
                </span>
              </span>
            </button></Hint>
          ))}
          {creatable && (
            <Hint label={'Create it and use it'}><button
              type="button"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
              onClick={() => {
                onCreate?.(query.trim());
                setQuery('');
              }}
            >
              <Plus className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">
                {createLabel} <span className="font-medium">“{query.trim()}”</span>
              </span>
            </button></Hint>
          )}
        </div>
      )}

      {bulkDelete && (
        <ConfirmDialog
          open={confirming}
          onOpenChange={(open) => {
            setConfirming(open);
            if (!open) setTyped('');
          }}
          title={`Delete ${tickedItems.length} ${bulkDelete.noun}?`}
          description={bulkDelete.description}
          confirmLabel={`Delete ${tickedItems.length}`}
          destructive
          pending={bulkBusy}
          confirmDisabled={typed.trim().toLowerCase() !== 'delete'}
          onConfirm={() => {
            setConfirming(false);
            setTyped('');
            void runBulkDelete();
          }}
        >
          {/* Every name, the way a cloud console lists the resources it is about to
              remove: "12 selected" is easy to confirm without noticing the wrong one. */}
          <div className="space-y-3">
            <p className="text-sm font-medium">
              These {tickedItems.length} {bulkDelete.noun} will be deleted:
            </p>
            <ul className="max-h-56 space-y-1 overflow-y-auto rounded-md border bg-muted/40 p-2 text-sm">
              {tickedItems.map((item) => (
                <li key={item.id} className="flex items-baseline gap-2">
                  <Trash2 className="size-3.5 shrink-0 translate-y-0.5 text-destructive" />
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{item.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {item.subtitle}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="space-y-1.5">
              <label htmlFor="bulk-delete-confirm" className="text-sm">
                To confirm, type <span className="font-mono font-semibold">delete</span>
              </label>
              <Input
                id="bulk-delete-confirm"
                autoComplete="off"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="delete"
              />
            </div>
          </div>
        </ConfirmDialog>
      )}
    </div>
  );
}
